import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WEBApi } from '../API/WEBApi';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import ImageSelector from '../Components/ImageSelector';

interface Blog {
  _id: string;
  title: string;
  author: string;
  content: string;
  image?: string;
  blob?: string; // ✅ Added blob field
}

const EditBlog: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');
  const [blob, setBlob] = useState(''); // ✅ blob state
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    WEBApi.getBlogById(id)
      .then((blog: Blog) => {
        setTitle(blog.title);
        setAuthor(blog.author);
        setContent(blog.content);
        setImage(blog.image || '');
        setBlob(blog.blob || ''); // ✅ populate blob
      })
      .catch(() => setError('Failed to load blog data.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) {
      setError('Invalid blog ID.');
      return;
    }
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      await WEBApi.updateBlog(id, { title, author, content, image, blob }); // ✅ include blob
      setMessage('Blog updated successfully!');
      setTimeout(() => navigate('/breathe-ev-admin/blogs'), 1500);
    } catch {
      setError('Failed to update blog. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <h3>Edit Blog</h3>
      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="title" className="form-label">Title</label>
          <input
            id="title"
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="author" className="form-label">Author</label>
          <input
            id="author"
            type="text"
            className="form-control"
            value={author}
            onChange={e => setAuthor(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Select Image</label>
          <ImageSelector
            selectedUrl={image}
            onSelect={(url) => setImage(url)}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="blob" className="form-label">Blob</label>
          <textarea
            id="blob"
            className="form-control"
            value={blob}
            onChange={(e) => setBlob(e.target.value)}
            rows={4}
            disabled={loading}
            placeholder="Blob string or data here..."
          />
        </div>

        <div className="mb-3">
          <label htmlFor="content" className="form-label">Content</label>
          <RichTextEditor value={content} onChange={setContent} />
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Updating...' : 'Update Blog'}
        </button>
      </form>
    </div>
  );
};

export default EditBlog;
